import { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Chip,
    CircularProgress,
    Divider,
    Grid,
    IconButton,
    Link,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
    useTheme
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import DownloadIcon from '@mui/icons-material/Download';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import DialogTitleDefault from '../default/DialogTitleDefault';
import { ButtonBasic } from '../../components';
import ServicioService from '../../services/servicios.service';
import FechaFunction from '../../function/FechaFunction';

const formatMoney = (value, currency = 'ARS') => {
    const num = Number(value || 0);
    try {
        return new Intl.NumberFormat('es-AR', {
            style: 'currency',
            currency,
            minimumFractionDigits: 2
        }).format(num);
    } catch (e) {
        return `$ ${num.toFixed(2)}`;
    }
};

const formatDate = (value) => {
    if (!value) return '-';
    try {
        return FechaFunction.format(new Date(value), 'dd/MM/yyyy');
    } catch (e) {
        return value;
    }
};

const ModalSaldoPendiente = ({ show, hideModal, servicios, servicioId }) => {
    const theme = useTheme();
    const isDarkMode = theme.palette.mode === 'dark';
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [resultados, setResultados] = useState([]);
    const [totalDeuda, setTotalDeuda] = useState(0);
    const [expanded, setExpanded] = useState(null);

    const listaServicios = useMemo(() => {
        if (Array.isArray(servicios) && servicios.length > 0) {
            return servicios.filter(Boolean);
        }
        if (servicioId) return [servicioId];
        return [];
    }, [servicios, servicioId]);

    const handleClose = () => hideModal();

    const cargar = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const resp = await ServicioService.getSaldoServicios(listaServicios);
            if (resp && resp?.success !== false) {
                const arr = Array.isArray(resp?.resultados) ? resp.resultados : [];
                setResultados(arr);
                setTotalDeuda(Number(resp?.total_deuda || 0));
                const primeroConDeuda = arr.find(r => r?.success && Number(r?.data?.saldo_total || 0) > 0);
                setExpanded(primeroConDeuda?.servicio || arr[0]?.servicio || null);
            } else {
                setError(resp?.msg || 'No se pudo obtener el saldo pendiente.');
            }
        } catch (e) {
            setError(e?.response?.data?.msg || e?.message || 'Error al consultar el saldo pendiente.');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (show && listaServicios.length > 0) {
            cargar();
        }
    }, [show, listaServicios]);

    const handleChangeAccordion = (servicioKey) => (_evt, isExpanded) => {
        setExpanded(isExpanded ? servicioKey : null);
    };

    const headerCellSx = {
        color: isDarkMode ? '#e0e0e0' : '#15294b',
        fontWeight: 'bold',
        borderColor: isDarkMode ? '#3d4a62' : '#e0e0e0',
        background: isDarkMode ? '#2d3a52' : '#f5f5f5'
    };
    const bodyCellSx = {
        color: isDarkMode ? '#e0e0e0' : '#15294b',
        borderColor: isDarkMode ? '#3d4a62' : '#e0e0e0'
    };

    const cantidadServicios = resultados.length;
    const cantidadConDeuda = resultados.filter(r => r?.success && Number(r?.data?.saldo_total || 0) > 0).length;

    return (
        <Dialog
            open={show}
            onClose={handleClose}
            disableEscapeKeyDown
            maxWidth='md'
            fullWidth
            sx={{ backdropFilter: 'blur(0.4px)' }}
        >
            <Box sx={{ background: isDarkMode ? '#1F2A40' : '#fff' }}>
                <DialogTitleDefault id="saldo-pendiente-priv" onClose={handleClose}>
                    <Typography
                        sx={{ flex: 1, textAlign: 'center', color: isDarkMode ? '#fff' : '#000', fontWeight: 'bold' }}
                        variant='h5'
                    >
                        Saldo pendiente {listaServicios.length > 1 ? 'de los servicios' : 'del servicio'}
                    </Typography>
                </DialogTitleDefault>
                <Divider sx={{ background: isDarkMode ? '#3d4a62' : 'gray' }} />
                <DialogContent>
                    {listaServicios.length === 0 && (
                        <Typography sx={{ color: isDarkMode ? '#b0bec5' : '#6b788e', textAlign: 'center', py: 2 }}>
                            No hay servicios para consultar.
                        </Typography>
                    )}
                    {isLoading && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4, flexDirection: 'column' }}>
                            <CircularProgress />
                            <Typography sx={{ mt: 2, color: isDarkMode ? '#e0e0e0' : '#15294b' }}>
                                Consultando saldo pendiente...
                            </Typography>
                        </Box>
                    )}
                    {!isLoading && error && (
                        <Box sx={{ p: 2 }}>
                            <Typography sx={{ color: '#d32f2f', textAlign: 'center' }}>
                                {error}
                            </Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                                <ButtonBasic
                                    textButton='Reintentar'
                                    variant='wk'
                                    color='nextStep'
                                    onClick={cargar}
                                />
                            </Box>
                        </Box>
                    )}
                    {!isLoading && !error && resultados.length > 0 && (
                        <Box>
                            <Box
                                sx={{
                                    background: isDarkMode ? '#2d3a52' : '#f1f5fb',
                                    border: 1,
                                    borderColor: isDarkMode ? '#3d4a62' : '#dbe5f1',
                                    borderRadius: 2,
                                    p: 2,
                                    mb: 2,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    flexWrap: 'wrap',
                                    gap: 1
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <PaidOutlinedIcon sx={{ color: isDarkMode ? '#90caf9' : '#15294b', mr: 1, fontSize: 32 }} />
                                    <Box>
                                        <Typography sx={{ color: isDarkMode ? '#b0bec5' : '#6b788e', fontSize: 13 }}>
                                            Deuda total {cantidadServicios > 1 ? `(${cantidadServicios} servicios)` : ''}
                                        </Typography>
                                        <Typography
                                            sx={{
                                                color: totalDeuda > 0
                                                    ? (isDarkMode ? '#ef9a9a' : '#c62828')
                                                    : (isDarkMode ? '#a5d6a7' : '#2e7d32'),
                                                fontWeight: 'bold'
                                            }}
                                            variant='h4'
                                        >
                                            {formatMoney(totalDeuda)}
                                        </Typography>
                                    </Box>
                                </Box>
                                {cantidadServicios > 1 && (
                                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                        <Chip
                                            label={`${cantidadConDeuda} con deuda`}
                                            color={cantidadConDeuda > 0 ? 'error' : 'success'}
                                            variant='outlined'
                                            size='small'
                                        />
                                        <Chip
                                            label={`${cantidadServicios - cantidadConDeuda} al día`}
                                            color='success'
                                            variant='outlined'
                                            size='small'
                                        />
                                    </Box>
                                )}
                            </Box>

                            {resultados.map((r) => {
                                const key = r?.servicio;
                                const ok = r?.success;
                                const info = r?.data;
                                const saldoServ = Number(info?.saldo_total || 0);
                                const facturas = info?.facturas || [];
                                const tieneDeuda = saldoServ > 0;
                                return (
                                    <Accordion
                                        key={key}
                                        expanded={expanded === key}
                                        onChange={handleChangeAccordion(key)}
                                        sx={{
                                            background: isDarkMode ? '#1F2A40' : '#fff',
                                            border: 1,
                                            borderColor: isDarkMode ? '#3d4a62' : '#e0e0e0',
                                            borderRadius: 2,
                                            mb: 1,
                                            '&:before': { display: 'none' }
                                        }}
                                        disableGutters
                                    >
                                        <AccordionSummary
                                            expandIcon={<ExpandMoreIcon sx={{ color: isDarkMode ? '#e0e0e0' : '#15294b' }} />}
                                            sx={{
                                                background: ok
                                                    ? (tieneDeuda
                                                        ? (isDarkMode ? '#3c1f1f' : '#fce7e7')
                                                        : (isDarkMode ? '#1a3a2a' : '#e6f8f2'))
                                                    : (isDarkMode ? '#3a2f1f' : '#fff4e5'),
                                                borderRadius: 2
                                            }}
                                        >
                                            <Grid container spacing={1} alignItems='center'>
                                                <Grid item xs={12} md={4}>
                                                    <Typography sx={{ color: isDarkMode ? '#b0bec5' : '#6b788e', fontSize: 12 }}>Servicio</Typography>
                                                    <Typography sx={{ color: isDarkMode ? '#e0e0e0' : '#15294b', fontWeight: 'bold' }}>
                                                        {`#${info?.servicio || key}`}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={12} md={4}>
                                                    <Typography sx={{ color: isDarkMode ? '#b0bec5' : '#6b788e', fontSize: 12 }}>Cliente</Typography>
                                                    <Typography sx={{ color: isDarkMode ? '#e0e0e0' : '#15294b', fontWeight: 'bold' }} noWrap>
                                                        {info?.cliente || '-'}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={12} md={4} sx={{ textAlign: { xs: 'left', md: 'right' } }}>
                                                    {ok ? (
                                                        <>
                                                            <Typography sx={{ color: isDarkMode ? '#b0bec5' : '#6b788e', fontSize: 12 }}>Saldo</Typography>
                                                            <Typography
                                                                sx={{
                                                                    color: tieneDeuda
                                                                        ? (isDarkMode ? '#ef9a9a' : '#c62828')
                                                                        : (isDarkMode ? '#a5d6a7' : '#2e7d32'),
                                                                    fontWeight: 'bold'
                                                                }}
                                                                variant='h6'
                                                            >
                                                                {formatMoney(saldoServ)}
                                                            </Typography>
                                                        </>
                                                    ) : (
                                                        <Chip
                                                            icon={<ErrorOutlineIcon />}
                                                            label='Error al consultar'
                                                            color='warning'
                                                            size='small'
                                                        />
                                                    )}
                                                </Grid>
                                            </Grid>
                                        </AccordionSummary>
                                        <AccordionDetails sx={{ pt: 2 }}>
                                            {!ok && (
                                                <Typography sx={{ color: '#d32f2f', textAlign: 'center' }}>
                                                    {r?.error || 'No se pudo obtener la información de este servicio.'}
                                                </Typography>
                                            )}
                                            {ok && (
                                                <>
                                                    <Grid container spacing={1} sx={{ mb: 2 }} alignItems='center'>
                                                        <Grid item xs={6} md={3}>
                                                            <Typography sx={{ color: isDarkMode ? '#b0bec5' : '#6b788e', fontSize: 12 }}>Servicio</Typography>
                                                            <Typography sx={{ color: isDarkMode ? '#e0e0e0' : '#15294b', fontWeight: 'bold' }}>{info?.servicio || '-'}</Typography>
                                                        </Grid>
                                                        <Grid item xs={6} md={3}>
                                                            <Typography sx={{ color: isDarkMode ? '#b0bec5' : '#6b788e', fontSize: 12 }}>Cuenta</Typography>
                                                            <Typography sx={{ color: isDarkMode ? '#e0e0e0' : '#15294b', fontWeight: 'bold' }}>{info?.cuenta || '-'}</Typography>
                                                        </Grid>
                                                        <Grid item xs={12} md={6} sx={{ textAlign: { xs: 'left', md: 'right' } }}>
                                                            {info?.link_pago && (
                                                                <ButtonBasic
                                                                    textButton='Link de pago'
                                                                    iconStart={<OpenInNewIcon />}
                                                                    variant='wk'
                                                                    color='nextStep'
                                                                    onClick={() => window.open(info.link_pago, '_blank', 'noopener,noreferrer')}
                                                                />
                                                            )}
                                                        </Grid>
                                                    </Grid>
                                                    <Typography sx={{ color: isDarkMode ? '#e0e0e0' : '#15294b', fontWeight: 'bold', mb: 1 }}>
                                                        Facturas
                                                    </Typography>
                                                    {facturas.length === 0 ? (
                                                        <Typography sx={{ color: isDarkMode ? '#b0bec5' : '#6b788e', textAlign: 'center', py: 2 }}>
                                                            No se encontraron facturas.
                                                        </Typography>
                                                    ) : (
                                                        <TableContainer
                                                            sx={{
                                                                border: 1,
                                                                borderColor: isDarkMode ? '#3d4a62' : '#e0e0e0',
                                                                borderRadius: 2,
                                                                maxHeight: 320
                                                            }}
                                                        >
                                                            <Table stickyHeader size='small'>
                                                                <TableHead>
                                                                    <TableRow>
                                                                        <TableCell sx={headerCellSx}>Código</TableCell>
                                                                        <TableCell sx={headerCellSx}>Número</TableCell>
                                                                        <TableCell sx={headerCellSx}>Fecha</TableCell>
                                                                        <TableCell sx={headerCellSx}>Vto.</TableCell>
                                                                        <TableCell align='right' sx={headerCellSx}>Monto $</TableCell>
                                                                        <TableCell align='right' sx={headerCellSx}>Monto USD</TableCell>
                                                                        <TableCell align='right' sx={headerCellSx}>Saldo pendiente</TableCell>
                                                                        <TableCell align='center' sx={headerCellSx}>Archivo</TableCell>
                                                                    </TableRow>
                                                                </TableHead>
                                                                <TableBody>
                                                                    {facturas.map((f, idx) => {
                                                                        const pendiente = Number(f?.saldo_pendiente || 0);
                                                                        return (
                                                                            <TableRow key={`${f?.codigo}-${f?.numero}-${idx}`} hover>
                                                                                <TableCell sx={bodyCellSx}>{f?.codigo || '-'}</TableCell>
                                                                                <TableCell sx={bodyCellSx}>{f?.numero || '-'}</TableCell>
                                                                                <TableCell sx={bodyCellSx}>{formatDate(f?.fecha)}</TableCell>
                                                                                <TableCell sx={bodyCellSx}>{formatDate(f?.vto)}</TableCell>
                                                                                <TableCell align='right' sx={bodyCellSx}>{formatMoney(f?.monto_pesos)}</TableCell>
                                                                                <TableCell align='right' sx={bodyCellSx}>{formatMoney(f?.monto_dolares, 'USD')}</TableCell>
                                                                                <TableCell
                                                                                    align='right'
                                                                                    sx={{
                                                                                        ...bodyCellSx,
                                                                                        color: pendiente > 0
                                                                                            ? (isDarkMode ? '#ef9a9a' : '#c62828')
                                                                                            : (isDarkMode ? '#a5d6a7' : '#2e7d32'),
                                                                                        fontWeight: 'bold'
                                                                                    }}
                                                                                >
                                                                                    {formatMoney(pendiente)}
                                                                                </TableCell>
                                                                                <TableCell align='center' sx={bodyCellSx}>
                                                                                    {f?.url_descarga ? (
                                                                                        <Tooltip title={f?.archivo || 'Descargar'}>
                                                                                            <IconButton
                                                                                                size='small'
                                                                                                component={Link}
                                                                                                href={f.url_descarga}
                                                                                                target='_blank'
                                                                                                rel='noopener noreferrer'
                                                                                                sx={{ color: isDarkMode ? '#90caf9' : '#15294b' }}
                                                                                            >
                                                                                                <DownloadIcon fontSize='small' />
                                                                                            </IconButton>
                                                                                        </Tooltip>
                                                                                    ) : '-'}
                                                                                </TableCell>
                                                                            </TableRow>
                                                                        );
                                                                    })}
                                                                </TableBody>
                                                            </Table>
                                                        </TableContainer>
                                                    )}
                                                </>
                                            )}
                                        </AccordionDetails>
                                    </Accordion>
                                );
                            })}
                        </Box>
                    )}
                </DialogContent>
                <Divider sx={{ background: isDarkMode ? '#3d4a62' : 'gray' }} />
                <DialogActions sx={{ display: 'flex', justifyContent: 'center' }}>
                    <ButtonBasic
                        textButton='Cerrar'
                        variant='wk'
                        color='cancel'
                        onClick={handleClose}
                    />
                </DialogActions>
            </Box>
        </Dialog>
    );
};

ModalSaldoPendiente.propTypes = {
    show: PropTypes.bool,
    hideModal: PropTypes.func,
    servicios: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
    servicioId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

ModalSaldoPendiente.defaultProps = {
    show: false,
    servicios: null,
    servicioId: null
};

export default ModalSaldoPendiente;
